import { useState, type FormEvent } from 'react'
import { useLocation, useNavigate, useParams } from 'react-router-dom'
import {
  Alert,
  Box,
  Button,
  Checkbox,
  FormControlLabel,
  IconButton,
  InputAdornment,
  Link,
  Paper,
  TextField,
  Typography,
} from '@mui/material'
import VisibilityIcon from '@mui/icons-material/VisibilityOutlined'
import VisibilityOffIcon from '@mui/icons-material/VisibilityOffOutlined'
import { ApiError } from '../api/client.js'
import { useSessionStore } from '../stores/sessionStore.js'
import { OubliMotDePasseDialog } from '../features/auth/OubliMotDePasseDialog.js'

/** Écran de connexion (W-02). */
export function LoginPage(): JSX.Element {
  const navigate = useNavigate()
  const location = useLocation()
  const login = useSessionStore((s) => s.login)
  /**
   * La boutique, quand on entre par elle — `/s/dstress/login`.
   *
   * ⭐ C'est ce qui rend possible de loger les comptes dans les schémas : sans
   * elle, le serveur ne saurait pas où vérifier le mot de passe, puisqu'une
   * même adresse peut exister dans plusieurs enseignes.
   */
  const { slug: boutique } = useParams<{ slug: string }>()

  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [showPassword, setShowPassword] = useState(false)
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [fields, setFields] = useState<Record<string, string>>({})
  const [oubli, setOubli] = useState(false)

  async function handleSubmit(event: FormEvent): Promise<void> {
    event.preventDefault()
    setSubmitting(true)
    setError(null)
    setFields({})
    try {
      await login(email, password, boutique)
      // ⚠️ Un compte d'ÉQUIPE n'a qu'une boutique : le sélecteur n'aurait rien
      // à lui proposer. On entre donc directement dans l'enseigne, alors qu'un
      // compte de plateforme passe par le sélecteur — il peut en avoir
      // plusieurs.
      const from = (location.state as { from?: string } | null)?.from
      navigate(boutique ? `/s/${boutique}/dashboard` : (from ?? '/s'), { replace: true })
    } catch (err) {
      if (err instanceof ApiError) {
        setError(err.message)
        setFields(err.fieldErrors())
      } else {
        // Panne réseau : distinguer clairement d'un refus d'identifiants.
        setError('Serveur injoignable. Vérifiez votre connexion.')
      }
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'grid',
        placeItems: 'center',
        bgcolor: 'background.default',
        p: 4,
      }}
    >
      <Paper sx={{ p: 8, borderRadius: 3, width: '100%', maxWidth: 420 }}>
        <Box sx={{ textAlign: 'center', mb: 6 }}>
          <Box
            sx={{
              width: 56,
              height: 56,
              borderRadius: 3,
              bgcolor: 'primary.main',
              color: '#fff',
              display: 'grid',
              placeItems: 'center',
              fontWeight: 700,
              fontSize: 20,
              mx: 'auto',
              mb: 4,
            }}
          >
            IO
          </Box>
          <Typography variant="h2">Connexion</Typography>
          <Typography variant="body2" sx={{ color: 'text.secondary', mt: 1 }}>
            Gestion commerciale multi-boutiques
          </Typography>
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 4 }} role="alert">
            {error}
          </Alert>
        )}

        <Box component="form" onSubmit={handleSubmit} noValidate>
          <TextField
            label="Adresse e-mail"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            error={Boolean(fields['email'])}
            helperText={fields['email']}
            fullWidth
            required
            autoFocus
            autoComplete="username"
            sx={{ mb: 4 }}
          />

          <TextField
            label="Mot de passe"
            type={showPassword ? 'text' : 'password'}
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            error={Boolean(fields['password'])}
            helperText={fields['password']}
            fullWidth
            required
            autoComplete="current-password"
            sx={{ mb: 3 }}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    onClick={() => setShowPassword((v) => !v)}
                    edge="end"
                    aria-label={
                      showPassword ? 'Masquer le mot de passe' : 'Afficher le mot de passe'
                    }
                  >
                    {showPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />

          <Box
            sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 5 }}
          >
            {/* La session dure 30 jours via le cookie de rafraîchissement :
                la case reflète ce comportement, elle ne le pilote pas encore. */}
            <FormControlLabel
              control={<Checkbox defaultChecked size="small" />}
              label={<Typography variant="body2">Rester connecté</Typography>}
            />
            <Link
              component="button"
              type="button"
              variant="body2"
              underline="hover"
              onClick={() => setOubli(true)}
            >
              Mot de passe oublié ?
            </Link>
          </Box>

          <Button
            type="submit"
            variant="contained"
            size="large"
            fullWidth
            disabled={submitting}
            // Largeur figée par fullWidth : le libellé change sans faire sauter la mise en page.
          >
            {submitting ? 'Connexion…' : 'Se connecter'}
          </Button>
        </Box>
      </Paper>

      {oubli && (
        <OubliMotDePasseDialog
          boutique={boutique}
          emailInitial={email}
          onClose={() => setOubli(false)}
        />
      )}
    </Box>
  )
}
